import { FileText, Package } from "lucide-react";
import { AntennaMark, Icon } from "@cntrl/ui";
import { useBandContextOrNull, useCapability } from "../../app/bandContext.js";
import { pushToast } from "../../app/toasts.js";
import { ANTENNA_CLUB_URL, PUBLIC_WEB_URL } from "../../config/env.js";
import { useEpk } from "../../app/hooks/useEpk.js";
import { useClubSettings } from "../../app/hooks/useFanClub.js";
import styles from "./AssetsShareBar.module.scss";

export interface AssetsShareBarProps {
  /** Navigate within the SPA (the Advance Docs tile). Provided by the screen. */
  onNavigate?: (route: string) => void;
}

/**
 * The phone-width tile bar (CD mobile Assets: "mobile leads with a tile bar" — no desktop
 * equivalent, where the EPK link lives in the share hero instead). An ink container holding up to
 * three tiles: **EPK** (share the public press-kit link), **Advance Docs** (the venue-facing pack)
 * and the crimson **Antenna** (fan signup link).
 *
 * Each tile is capability-gated and owns its own query, so a seat without the capability fires no
 * request; a tile whose data doesn't exist yet doesn't render, and the bar `:empty`-collapses.
 *
 * CD Pro rule, recorded for when plans land: standard = 3 tiles, Pro = 2 (Advance Docs drops — on
 * Pro that surface owns venue-facing), and while the plan is UNKNOWN render 2, never 3-then-yank.
 * We have no plan concept yet AND `/advance` is a real page for every ARTIST-shell seat, so the tile
 * simply navigates there, gated like the page itself — plus the shell, since the roster started
 * mounting this page (2026-09-01) and has no `/advance` of its own. A tile pointing at a route the
 * mounted shell does not have is not a broken link, it is a lens crossing: it would drop an org seat
 * into the artist shell with no band. (Giving the roster its own Advance Docs is the nav-parity CR's
 * Touring▾ item — when that lands, this gate is what should change, not the tile.)
 */
export function AssetsShareBar({ onNavigate }: AssetsShareBarProps) {
  const canEpk = useCapability("epk:write");
  const canProduction = useCapability("production:read");
  const canAntenna = useCapability("antenna:manage");
  const hasAdvanceRoute = useBandContextOrNull()?.shell !== "roster";

  return (
    <nav className={styles.bar} aria-label="Assets shortcuts">
      {canEpk && <EpkTile />}
      {canProduction && hasAdvanceRoute && (
        <button type="button" className={styles.tile} onClick={() => onNavigate?.("/advance")}>
          <span className={styles.badge} aria-hidden="true"><Icon icon={FileText} size={20} /></span>
          <span className={styles.tileLabel}>Advance Docs</span>
        </button>
      )}
      {canAntenna && <AntennaTile />}
    </nav>
  );
}

/**
 * Platform sheet first (the WKWebView/Safari native sheet), clipboard as the degrade — the CD share
 * order. Only a USER-CANCELLED share is silent; any other failure falls through to the clipboard so
 * the tap always produces visible feedback.
 */
async function shareLink(url: string, title: string) {
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({ title, url });
      pushToast("Link shared.", "success");
      return;
    } catch (err) {
      // The user dismissed the sheet — that is not a failure, and must not toast one.
      if (err instanceof DOMException && err.name === "AbortError") return;
      // Anything else (no permission, insecure context, unsupported payload) degrades below.
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    pushToast("Link copied.", "success");
  } catch {
    pushToast("Couldn't copy the link.", "error");
  }
}

function EpkTile() {
  const epk = useEpk();
  const url = epk.data?.publicPath ? `${PUBLIC_WEB_URL}${epk.data.publicPath}` : null;
  if (!url) return null;
  return (
    <button type="button" className={styles.tile} onClick={() => void shareLink(url, "Press kit")}>
      <span className={styles.badge} aria-hidden="true"><Icon icon={Package} size={20} /></span>
      <span className={styles.tileLabel}>EPK</span>
    </button>
  );
}

function AntennaTile() {
  const club = useClubSettings();
  const handle = club.data?.handle;
  if (!handle) return null;
  const url = `${ANTENNA_CLUB_URL}/${handle}`;
  return (
    <button
      type="button"
      className={`${styles.tile} ${styles.tileAccent}`}
      onClick={() => void shareLink(url, "Join our Antenna")}
    >
      <span className={styles.badge} aria-hidden="true"><AntennaMark size={20} /></span>
      <span className={styles.tileLabel}>Antenna</span>
    </button>
  );
}
